import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tag, CheckCircle2, XCircle, Filter, Wine, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const STATUSES = [
  { v: "pending_approval", label: "In attesa" },
  { v: "approved", label: "Approvati" },
  { v: "rejected", label: "Rifiutati" },
  { v: "sold", label: "Venduti" },
  { v: "withdrawn", label: "Ritirati" },
];

export default function AdminB2BLots() {
  const [lots, setLots] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [filter, setFilter] = useState("pending_approval");
  const [loading, setLoading] = useState(true);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionType, setActionType] = useState("approve"); // approve|reject
  const [activeLot, setActiveLot] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/b2b/lots${filter ? `?status=${filter}` : ""}`);
      setLots(r.data.lots || []);
      setByStatus(r.data.by_status || {});
    } catch { toast.error("Errore caricamento"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openAction = (lot, type) => { setActiveLot(lot); setActionType(type); setAdminNotes(""); setActionOpen(true); };

  const confirmAction = async () => {
    try {
      await api.post(`/admin/b2b/lots/${activeLot.id}/${actionType}`, { admin_notes: adminNotes });
      toast.success(actionType === "approve" ? "Lotto approvato" : "Lotto rifiutato");
      setActionOpen(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div data-testid="admin-b2b-lots-page" className="p-6 lg:p-10">
      <div className="flex items-center gap-3 mb-2">
        <Tag className="h-6 w-6 text-[hsl(var(--primary))]" />
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold">Lotti B2B</div>
      </div>
      <h1 className="editorial-heading text-3xl md:text-4xl mb-2">Marketplace lotti rivenditori</h1>
      <p className="text-muted-foreground mb-6">Approva o rifiuta i lotti pubblicati dai rivenditori B2B prima che diventino visibili.</p>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {STATUSES.map((s) => (
          <button
            key={s.v}
            onClick={() => setFilter(s.v)}
            data-testid={`admin-lots-filter-${s.v}`}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === s.v ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-transparent border-border hover:border-[hsl(var(--primary))]"
            }`}
          >
            {s.label} <span className="ml-1 opacity-70">({byStatus[s.v] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Caricamento…</div>
      ) : lots.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center text-muted-foreground">Nessun lotto in questa categoria.</div>
      ) : (
        <div className="space-y-3">
          {lots.map((lot) => (
            <div key={lot.id} className="bg-card border border-border rounded-sm p-5" data-testid={`admin-lot-${lot.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    {lot.product_type === "wine" ? <Wine className="h-4 w-4 text-[#7A1C2B]" /> : <Package className="h-4 w-4 text-[#C9A961]" />}
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{lot.product_type}</span>
                  </div>
                  <h3 className="font-semibold text-lg">{lot.title}</h3>
                  <div className="text-xs text-muted-foreground mt-1">
                    Venditore: <strong>{lot.seller_company}</strong> · {lot.seller_email}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2 line-clamp-2">{lot.description}</div>
                  <div className="flex flex-wrap gap-3 mt-3 text-xs">
                    <span><strong>Qty:</strong> {lot.quantity}</span>
                    <span><strong>Prezzo:</strong> €{lot.price_per_unit?.toFixed(2)}</span>
                    <span><strong>Totale:</strong> €{lot.total_price?.toFixed(2)}</span>
                    {lot.region && <span><strong>Regione:</strong> {lot.region}</span>}
                    {lot.vintage && <span><strong>Annata:</strong> {lot.vintage}</span>}
                    {lot.location && <span><strong>Magazzino:</strong> {lot.location}</span>}
                  </div>
                  {lot.admin_notes && (
                    <div className="mt-2 text-xs italic text-muted-foreground border-l-2 border-border pl-2">Note: {lot.admin_notes}</div>
                  )}
                </div>
                {lot.status === "pending_approval" && (
                  <div className="flex gap-2">
                    <button onClick={() => openAction(lot, "approve")} data-testid={`approve-${lot.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> Approva
                    </button>
                    <button onClick={() => openAction(lot, "reject")} data-testid={`reject-${lot.id}`} className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-sm hover:bg-red-700">
                      <XCircle className="h-4 w-4" /> Rifiuta
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{actionType === "approve" ? "Approva lotto" : "Rifiuta lotto"}</DialogTitle></DialogHeader>
          {activeLot && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{activeLot.title}</div>
              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Note {actionType === "reject" ? "motivazione" : "(opz.)"}</span>
                <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={4} className="w-full px-3 py-2 border border-border rounded-sm" data-testid="admin-lot-notes" />
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setActionOpen(false)} className="px-4 py-2 text-sm">Annulla</button>
            <button onClick={confirmAction} data-testid="admin-lot-confirm" className={`px-4 py-2 text-sm text-white font-semibold rounded-sm ${
              actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
            }`}>
              {actionType === "approve" ? "Conferma approvazione" : "Conferma rifiuto"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
